<script setup>
/* Imports */
import {
  computed,
  onMounted,
  ref,
  watch,
} from 'vue';

/* Affirm */
import {
  affirmPriceToCents,
  affirmPriceRange,
  affirmSupportedCountryCode,
  affirmSupportedLocale,
  affirmPublicApiKey,
  affirmScript,
} from '../helpers/affirm/config.js';

/* Props */
const props = defineProps({
  priceData: {
    type: Object,
    default: () => ({}),
  },
  setIsAffirmScriptLoaded: {
    type: Function,
    default: () => {},
  },
});

/* State */
const state = ref({
  isSkeletonLoading: true,
});

/* Computed property to calculate priceInCents dynamically */
const priceInCents = computed(() => affirmPriceToCents(props.priceData?.salePrice?.price || props.priceData?.listPrice?.price));

/* Check if the price falls within the Affirm display range */
const isAffirmEligible = computed(() => priceInCents.value >= affirmPriceRange?.MIN && priceInCents.value <= affirmPriceRange?.MAX);

/**
 * Check if the Affirm widget should be displayed
 * The Affirm widget should be displayed if:
 * - The price is available
 * - The price falls within the Affirm display range
 */
const shouldDisplayAffirm = computed(() => priceInCents.value && isAffirmEligible.value);

/* Check if the skeleton loader should be displayed */
const shouldShowSkeleton = computed(() => state.value.isSkeletonLoading && Boolean(shouldDisplayAffirm.value));

const loadAffirm = () => new Promise((resolve, reject) => {
  const timestamp = new Date().getTime(); // Get the current timestamp to avoid caching
  const affirmWidgetUrl = `${affirmScript}?t=${timestamp}`; // Append the timestamp as a query parameter

  /* eslint-disable */
  window._affirm_config = {
    public_api_key: affirmPublicApiKey,
    script: affirmWidgetUrl,
    locale: affirmSupportedLocale,
    country_code: affirmSupportedCountryCode,
  };

  (function (l, g, m, e, a, f, b) {
    let d; const
      c = l[m] || {};
    const h = document.createElement(f);
    const n = document.getElementsByTagName(f)[0];
    const k = (a, b, c) => () => a[b]._.push([c, arguments]);

    c[e] = k(c, e, 'set');
    d = c[e];
    c[a] = {};
    c[a]._ = [];
    d._ = [];
    c[a][b] = k(c, a, b);
    [
      'set',
      'add',
      'save',
      'post',
      'open',
      'empty',
      'reset',
      'on',
      'off',
      'trigger',
      'ready',
      'setProduct',
    ]
      .forEach((fn) => d[fn] = k(c, e, fn));
    [
      'get',
      'token',
      'url',
      'items',
    ].forEach((fn) => d[fn] = () => { });

    h.async = true;
    h.src = g[f];
    h.onload = () => {
      resolve(true);
      state.value.isSkeletonLoading = false;
    };
    h.onerror = () => {
      reject(new Error('Failed to load Affirm script'));
      state.value.isSkeletonLoading = false;
    };
    n.parentNode.insertBefore(h, n);

    delete g[f];
    d(g);
    l[m] = c;
  }(window, window._affirm_config, 'affirm', 'checkout', 'ui', 'script', 'ready'));
  /* eslint-enable */
});

/* Lifecycle hooks */
onMounted(async () => {
  try {
    await loadAffirm();
    props.setIsAffirmScriptLoaded(true);
  } catch (error) {
    props.setIsAffirmScriptLoaded(false);

    console.error('Failed to load Affirm onMounted:', error);
  }
});

/**
 * Watches for changes in the `priceData` prop.
 * This function ensures the Affirm widget is reloaded whenever the price data changes.
 * It also manages the skeleton loading state and tracks the loading status of the Affirm script.
 */
watch(() => props.priceData, async () => {
// Set the skeleton loading state to true to show a loading indicator when price data is changing
  state.value.isSkeletonLoading = true;
  try {
    // Reload the Affirm widget with the updated price data
    await loadAffirm();
    // Indicate that the Affirm script has loaded successfully
    props.setIsAffirmScriptLoaded(true);
  } catch (error) {
    console.error('Failed to load Affirm on watcher:', error);
    // If there's an error loading the script, set the loading state to false
    props.setIsAffirmScriptLoaded(false);
  }
});
</script>

<template>
  <div class="app__product__affirm__wrapper">
    <template v-if="shouldShowSkeleton">
      <g-skeleton
        class="app__product__skeleton"
        data-testid="affirm-widget-skeleton"
      />
    </template>
    <template v-else-if="shouldDisplayAffirm">
      <div
        class="app__product__affirm"
        data-testid="affirm-widget-container"
      >
        <p
          class="affirm-as-low-as"
          data-page-type="product"
          :data-amount="priceInCents"
          data-testid="affirm-widget"
        />
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.app__product {

  &__affirm {
    font-size: $font-size-small;
    max-width: fit-content;

    &__wrapper:not(:empty) {
      min-height: 1.313rem; // Set a minimum height to prevent layout shift when the widget loads
      margin-bottom: 0.125rem; // 2px margin below the widget for spacing
    }

    p {
      @include spacing('margin-top', 'none');
      @include spacing('margin-bottom', 'md');
    }

    > p:last-of-type:last-child {
      @include spacing('margin-bottom', 'none');
    }
  }

  &__skeleton {
    display: block;
    width: 100%;
    height: 1.313rem;
  }
}

// stylelint-disable-next-line selector-class-pattern
:deep(.__affirm-logo.__ligature__affirm_full_logo__) {
  font-size: 0.938rem; // 15px
}

:deep(.affirm-modal-trigger) {
  font-size: $font-size-tiny;
}
</style>
